import { createContact, deleteContact, getContactById, getContacts, updateContact } from "./apis";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { Contact, GetContactsResponse } from "./types";
import { useFeedbackAlerts } from "../_app/hooks";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useSingleContact(contactId: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["contact", { accountId, contactId }],
    queryFn: () => getContactById(accountId, contactId),
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useContacts(page: number, limit: number, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["contacts", { accountId, page, limit }],
    queryFn: () => getContacts(accountId, page, limit),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useContactsInfinite(pageSize: number = 25, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetContactsResponse>({
    queryKey: ["contacts-inf", { accountId }],
    queryFn: ({ pageParam }) => getContacts(accountId, pageParam as number, pageSize),
    initialPageParam: 0,
    getNextPageParam: ({ page, total }: any) => {
      if ((page + 1) * pageSize < total) {
        return page + 1;
      } else {
        return undefined;
      }
    },
    ...options,
  });
}

export function useContactCreate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (contact: Contact) => createContact(accountId, contact),
    ...options,

    onSuccess: (contact: any) => {
      queryClient.setQueryData(["contact", { accountId, contactId: contact.id }], () => contact);
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess("Your request has been submitted");
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useContactUpdate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (contact: Contact) => updateContact(accountId, contact),
    ...options,

    onSuccess: (contact: any) => {
      queryClient.invalidateQueries({
        queryKey: ["contact", { accountId, contactId: contact.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess("Your request has been submitted");
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useContactDelete(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (contact: Contact) => deleteContact(accountId, contact),

    onSuccess: (contact: any) => {
      queryClient.invalidateQueries({
        queryKey: ["contact", { accountId, contactId: contact.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });
      setFeedbackAlertSuccess("Contact deleted");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}
